import { href, hrefOnHost, type ParamsOf, type RouteDecl } from '@makan/contracts/client';
import { hostGroupOf } from './api.ts';

/**
 * A group's own host (organisation `add host`, cloud/src/http/hosts.ts): the
 * host names the group, so its public pages read `/join`, not `/join/<id>`.
 * The app asks which group once, before it renders (main.tsx).
 */
let held: string | null = null;

/** Asks the cloud which group this host is (`groupOf`: the page's own GET /api/host; a test names one). */
export async function loadHost(groupOf: () => Promise<string | null> = hostGroupOf): Promise<void> {
  held = await groupOf().catch(() => null);
}

/** The group whose own host this page is on, or null on the product's host. */
export function hostGroup(): string | null {
  return held;
}

/** The path of a public route, for a link or `navigate` on this page: without the group id on its group's own host. */
export function publicPath<D extends RouteDecl>(route: D, params: ParamsOf<D['path']>, query: Readonly<Record<string, string>> = {}): string {
  return held !== null && (params as Readonly<Record<string, string>>)['groupId'] === held ? hrefOnHost(route, params, query) : href(route, params, query);
}

/**
 * The full address of a public route, for a QR code, a printed card or a
 * shared link: on `host` (the group's own, when it holds one) with this
 * page's scheme and port, else on this page's origin.
 */
export function publicLink<D extends RouteDecl>(route: D, params: ParamsOf<D['path']>, query: Readonly<Record<string, string>> = {}, host: string | null = null): string {
  if (host === null) return new URL(publicPath(route, params, query), location.origin).href;
  const origin = `${location.protocol}//${host}${location.port === '' ? '' : `:${location.port}`}`;
  return new URL(hrefOnHost(route, params, query), origin).href;
}
